import { BusinessRating } from '@/components/Business'
import { SidePanel } from '@/components/SidePanel'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { usePlaces } from '@/hooks/usePlaces'
import { useCreateLocation } from '@/queries/locations/useCreateLocation'
import { useLocations } from '@/queries/locations/useLocations'
import { usePlaceDetails } from '@/queries/locations/usePlaceDetails'
import { getPlacesPhotoUrl } from '@/utils'
import { useStytchOrganization } from '@stytch/react/b2b'
import { Building2, CalendarDays, Globe, MapPin, Phone } from 'lucide-react'
import { enqueueSnackbar } from 'notistack'
import { useNavigate, useParams } from 'react-router-dom'

import type { Location, Workspace } from 'honeygrid-types'
import type { FC } from 'react'

interface LocationDetailsProps {
	place?: Location
	workspace?: Workspace
}

interface PlacePrediction {
	description: string
	place_id: string
}

export const LocationDetails: FC<LocationDetailsProps> = () => {
	const {
		handleSearch,
		hasSearched,
		setQuery,
		placePredictions,
		clearSearchResults,
		clearPlacePredictions,
		query,
	} = usePlaces(true)
	const { workspaceId = '', placeId = '' } = useParams()
	const { data: location } = usePlaceDetails(placeId)
	const { mutateAsync: createLocation } = useCreateLocation()
	const { organization } = useStytchOrganization<false>()
	const { data: locations } = useLocations({ workspaceId: organization?.organization_id })
	const navigate = useNavigate()

	const locationExists = location && locations?.some((l) => l.place_id === location.place_id)

	const handlePlaceSelect = async (placeId: string) => {
		navigate(`/${workspaceId}/locations/${placeId}`)
		setQuery('')
		clearSearchResults()
		clearPlacePredictions()
		hasSearched.current = false
	}

	const handleAddLocation = async () => {
		if (!location || locationExists) return
		try {
			await createLocation({
				location,
			})

			enqueueSnackbar('Location added to workspace', { variant: 'success' })
		} catch (error) {
			console.error('Failed to add location:', error)
			enqueueSnackbar('Failed to add location', { variant: 'error' })
		}
	}

	const renderLocationDetails = () => {
		if (!location) return null

		const {
			types,
			name,
			photos,
			rating,
			user_ratings_total,
			website,
			formatted_address,
			formatted_phone_number,
			opening_hours,
		} = location
		const [firstType = ''] = types ?? []
		const formattedWebsite = website ? new URL(website).origin + new URL(website).pathname : null

		return (
			<>
				<div className="w-full">
					<img
						src={getPlacesPhotoUrl((photos?.[0] as any)?.photo_reference)}
						width="100%"
						alt={name}
						className="w-full h-48 object-cover"
					/>
				</div>
				<div className="p-6">
					<h2 className="text-xl font-semibold mb-2">{name}</h2>
					<BusinessRating size="small" rating={rating} totalRatings={user_ratings_total} />
				</div>
				<div className="h-px bg-border" />
				<div className="p-4 flex-1 overflow-auto">
					<ul className="space-y-4">
						{firstType ? (
							<li className="flex items-center gap-2">
								<Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
								<p className="text-sm text-muted-foreground capitalize break-words">{firstType}</p>
							</li>
						) : null}
						{formattedWebsite ? (
							<li className="flex items-center gap-2">
								<Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
								<p className="text-sm text-muted-foreground break-all">{formattedWebsite}</p>
							</li>
						) : null}
						{formatted_address ? (
							<li className="flex items-center gap-2">
								<MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
								<p className="text-sm text-muted-foreground break-words">{formatted_address}</p>
							</li>
						) : null}
						{formatted_phone_number ? (
							<li className="flex items-center gap-2">
								<Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
								<p className="text-sm text-muted-foreground break-words">
									{formatted_phone_number}
								</p>
							</li>
						) : null}
						{opening_hours?.weekday_text?.length ? (
							<li className="flex items-start gap-2">
								<CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
								<div className="space-y-1">
									{opening_hours?.weekday_text?.map((entry) => (
										<p className="text-sm text-muted-foreground break-words" key={entry}>
											{entry}
										</p>
									))}
								</div>
							</li>
						) : null}
					</ul>
				</div>
			</>
		)
	}

	return (
		<SidePanel
			header={{
				title: 'Location Details',
				backEnabled: true,
			}}
			footer={{
				buttonLabel: locationExists ? 'Already in Workspace' : 'Add to Workspace',
				handler: handleAddLocation,
				disabled: locationExists,
			}}
		>
			<div className="flex flex-col h-full gap-4">
				<Command shouldFilter={false} className="rounded-lg border shadow-md">
					<CommandInput
						placeholder="Find your location"
						value={query}
						onValueChange={(value) => {
							setQuery(value)
							if (value.length > 0) {
								handleSearch()
							} else {
								clearSearchResults()
							}
						}}
					/>
					<CommandList>
						{hasSearched?.current && <CommandEmpty>No locations found</CommandEmpty>}
						{placePredictions.filter((prediction) => !!prediction.description).length > 0 && (
							<CommandGroup>
								{placePredictions.map((prediction: PlacePrediction) => (
									<CommandItem
										key={prediction.place_id}
										value={prediction.description}
										className="px-4 py-2"
										onSelect={() => handlePlaceSelect(prediction.place_id)}
									>
										<div className="flex flex-col gap-1 w-full">
											<div className="flex items-center justify-between">
												<span className="font-medium">{prediction.description.split(',')[0]}</span>
											</div>
											<span className="text-sm text-muted-foreground">
												{prediction.description.split(',').slice(1).join(',').trim()}
											</span>
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						)}
					</CommandList>
				</Command>
				<div className="flex flex-col flex-1">{renderLocationDetails()}</div>
			</div>
		</SidePanel>
	)
}
